import React, { useEffect, useState } from 'react';
import { getPressCoverage, getPressImageUrl, getPressSourceUrl } from '../../api/pressCoverageService';
import './PressCoverage.scss';

const PressImage = ({ url, title }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [url]);
  const src = getPressImageUrl(url);
  return src && !failed
    ? <img src={src} alt={title} loading="lazy" decoding="async" width="640" height="400" onError={() => setFailed(true)} />
    : <div className="press-coverage__placeholder" aria-hidden="true"><i className="pi pi-book" /></div>;
};

export default function PressCoverage() {
  const [filters, setFilters] = useState({ search: '', startDate: '', endDate: '' });
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ items: [], pageCount: 0, totalCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterError, setFilterError] = useState('');
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    getPressCoverage({ page, pageSize: 12, search: filters.search, startDate: filters.startDate || undefined, endDate: filters.endDate || undefined }, controller.signal)
      .then(data => {
        if (controller.signal.aborted) return;
        if (page > Math.max(1, data.pageCount)) setPage(Math.max(1, data.pageCount));
        setResult(data);
      })
      .catch(err => { if (!controller.signal.aborted) setError(err.response?.data?.resultMessage || err.message || 'İçerikler yüklenemedi.'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [filters, page, refresh]);

  const applyFilters = (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const next = { search: String(form.get('search') || '').trim(), startDate: String(form.get('startDate') || ''), endDate: String(form.get('endDate') || '') };
    if (next.startDate && next.endDate && next.startDate > next.endDate) {
      setFilterError('Başlangıç tarihi bitiş tarihinden sonra olamaz.');
      return;
    }
    setFilterError('');
    setPage(1);
    setFilters(next);
  };

  return (
    <section className="press-coverage">
      <div className="container">
        <header className="press-coverage__heading">
          <span>İSKENDERUN BELEDİYESİ</span>
          <h1>Basında Biz</h1>
          <p>İskenderun ve belediyemizin çalışmaları hakkında basında yer alan haberler.</p>
        </header>
        <form className="press-coverage__filters" onSubmit={applyFilters}>
          <label>Haber veya yayın kuruluşu<input name="search" type="search" maxLength={200} placeholder="Basında ara" /></label>
          <label>Başlangıç tarihi<input name="startDate" type="date" /></label>
          <label>Bitiş tarihi<input name="endDate" type="date" /></label>
          <button type="submit">Filtrele</button>
          <button type="reset" className="press-coverage__reset" onClick={() => { setPage(1); setFilters({ search: '', startDate: '', endDate: '' }); setFilterError(''); }}>Temizle</button>
        </form>
        {filterError && <p role="alert" className="press-coverage__error">{filterError}</p>}
        <div aria-live="polite" aria-busy={loading}>
          {loading ? <p className="press-coverage__state">Haberler yükleniyor...</p> : error ? (
            <div className="press-coverage__state" role="alert"><p>{error}</p><button onClick={() => setRefresh(n => n + 1)}>Tekrar dene</button></div>
          ) : !result.items.length ? <p className="press-coverage__state">{filters.search || filters.startDate || filters.endDate ? 'Filtrelerinize uygun haber bulunamadı.' : 'Henüz basın haberi eklenmedi.'}</p> : (
            <>
              <p className="press-coverage__count">{result.totalCount} haber</p>
              <div className="press-coverage__grid">
                {result.items.map(item => {
                  const link = getPressSourceUrl(item.sourceUrl);
                  return (
                    <article className="press-coverage__card" key={item.guid}>
                      <div className="press-coverage__image"><PressImage url={item.pictureUrl} title={item.title} /></div>
                      <div className="press-coverage__body">
                        <div className="press-coverage__meta"><strong>{item.sourceName}</strong><time dateTime={item.publishedAt}>{new Date(item.publishedAt).toLocaleDateString('tr-TR')}</time></div>
                        <h2>{item.title}</h2>
                        {item.summary && <p>{item.summary}</p>}
                        {link && <a href={link} target="_blank" rel="noopener noreferrer">Haberi kaynağında oku <span aria-hidden="true">↗</span><span className="visually-hidden"> (yeni sekmede açılır)</span></a>}
                      </div>
                    </article>
                  );
                })}
              </div>
              {result.pageCount > 1 && <nav className="press-coverage__pagination" aria-label="Basın haberleri sayfalama">
                <button disabled={page <= 1} onClick={() => setPage(n => n - 1)}>Önceki</button>
                <span>Sayfa {page} / {result.pageCount}</span>
                <button disabled={page >= result.pageCount} onClick={() => setPage(n => n + 1)}>Sonraki</button>
              </nav>}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
